import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Seo from '../components/Seo';
import Toast from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import { apiBaseUrl } from '../config';
import { useTurnstile } from '../hooks/useTurnstile';
import axios from 'axios';
import logo from '../assets/logo-main.png';
import './AuthPages.css';

const SunIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
    <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
  </svg>
);
const MoonIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
  </svg>
);

const FEATURES = [
  { title: 'All your courses, in one place', desc: 'Pick up exactly where you left off across every module.' },
  { title: 'Market tools at your fingertips', desc: 'SIP calculator, risk-reward calculator and more — built for disciplined market participation.' },
  { title: 'Insights that educate', desc: 'Blog articles and strategy breakdowns from experienced market educators.' },
];

const CheckIcon = () => (
  <svg viewBox="0 0 20 20" width="13" height="13" fill="none">
    <path d="M4 10.5l3.2 3.2L16 5.5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const Login = ({ theme, setTheme }) => {
  const [formData, setFormData]   = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError]         = useState('');
  const [loading, setLoading]     = useState(false);
  const { login }   = useAuth();
  const navigate    = useNavigate();
  const { getTurnstileToken, resetTurnstile, turnstileRef, turnstileInteractive } = useTurnstile();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    let turnstileToken;
    try {
      turnstileToken = await getTurnstileToken();
    } catch (err) {
      setError('Verification failed. Please wait a moment then try again.');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${apiBaseUrl}/user/signin`, { ...formData, turnstileToken }, { withCredentials: true });
      if (response.data.success) {
        resetTurnstile();
        await login(response.data.token);
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Sign in failed. Please try again.');
      resetTurnstile();
    } finally {
      setLoading(false);
    }
  };

  const year = new Date().getFullYear();
  const canSubmit = !loading && formData.email && formData.password;
  const showOverlay = loading && !turnstileInteractive;

  return (
    <div className="auth-split">
      <Seo title="Sign In — InvFly" description="Sign in to your InvFly account to access courses and tools." noIndex />
      <Toast message={error} type="error" onClose={() => setError('')} />

      {/* ── Brand pane ── */}
      <aside className="auth-split__brand">
        <div className="auth-split__brand-bg" aria-hidden="true" />

        <header className="auth-split__brand-header">
          <img src={logo} alt="InvFly" className="auth-split__brand-logo" />
        </header>

        <div className="auth-split__brand-body">
          <h1 className="auth-split__brand-title">
            Welcome back.
          </h1>
          <p className="auth-split__brand-sub">
            Your learning continues here — structured courses, practical tools, and educational content built around disciplined market participation.
          </p>
          <ul className="auth-split__features">
            {FEATURES.map((f) => (
              <li key={f.title} className="auth-split__feature">
                <span className="auth-split__feature-icon" aria-hidden="true"><CheckIcon /></span>
                <div>
                  <div className="auth-split__feature-title">{f.title}</div>
                  <div className="auth-split__feature-desc">{f.desc}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <footer className="auth-split__brand-footer">
          <span>© {year} InvFly Technologies</span>
          <span className="auth-split__brand-dot" aria-hidden="true">•</span>
          <span>Secure, encrypted sign in</span>
        </footer>
      </aside>

      {/* ── Form pane ── */}
      <main className="auth-split__form">
        <div className="auth-split__form-inner" style={{ position: 'relative' }}>

          {showOverlay && (
            <div className="auth-card-overlay">
              <div className="auth-card-overlay-spinner" />
              <span className="auth-card-overlay-text">Signing in…</span>
            </div>
          )}

          <div className="auth-split__form-logo">
            <img src={logo} alt="InvFly" />
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Link to="/" style={{ fontSize: '12px', color: 'var(--text-3)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
                Home
              </Link>
              {setTheme && (
                <button className="auth-split__theme-btn" onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')} title="Toggle theme">
                  {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
                </button>
              )}
            </div>
          </div>

          <span className="auth-split__eyebrow">Secure sign in</span>
          <h2 className="auth-split__title">Welcome Back</h2>
          <p className="auth-split__subtitle">Sign in for smarter trading</p>

          <form onSubmit={handleSubmit}>
            <div className="fg">
              <label className="fl" htmlFor="email">Email Address</label>
              <input className="fi" type="email" id="email" name="email" value={formData.email} onChange={handleChange} placeholder="you@email.com" required autoFocus autoComplete="email" />
            </div>

            <div className="fg">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <label className="fl" htmlFor="password">Password</label>
                <Link to="/forgotpassword" style={{ fontSize: 11, color: 'var(--accent)', textDecoration: 'none', fontWeight: 500 }}>Forgot password?</Link>
              </div>
              <div style={{ position: 'relative' }}>
                <input className="fi" type={showPassword ? 'text' : 'password'} id="password" name="password" value={formData.password} onChange={handleChange} placeholder="••••••••" required autoComplete="current-password" style={{ paddingRight: 42 }} />
                <button type="button" onClick={() => setShowPassword(s => !s)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', display: 'flex', padding: 4 }}>
                  {showPassword
                    ? <svg viewBox="0 0 20 20" width="16" height="16" fill="none"><path d="M3 3l14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><path d="M10 4.5c-4 0-7 3-8.5 5.5.7 1.2 1.8 2.6 3.3 3.7M16.5 13.7C18 12.6 18.8 11.2 19.5 10c-1.5-2.5-4.5-5.5-9.5-5.5-1 0-1.9.1-2.7.3" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/></svg>
                    : <svg viewBox="0 0 20 20" width="16" height="16" fill="none"><path d="M1.5 10C3 7.5 6 4.5 10 4.5s7 3 8.5 5.5c-1.5 2.5-4.5 5.5-8.5 5.5S3 12.5 1.5 10z" stroke="currentColor" strokeWidth="1.5" fill="none"/><circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.5" fill="none"/></svg>
                  }
                </button>
              </div>
            </div>

            <div ref={turnstileRef} className="turnstile-wrapper" aria-hidden="true" />

            <button type="submit" className="fsub" disabled={!canSubmit} style={{ marginTop: 8 }}>
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
          </form>

          <div className="auth-split__switch">
            Don't have an account? <Link to="/signup">Sign Up</Link>
          </div>

          <div className="auth-split__meta">
            <span className="auth-split__meta-icon" aria-hidden="true">
              <svg viewBox="0 0 20 20" width="12" height="12" fill="none">
                <rect x="4" y="9" width="12" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M7 9V6.5a3 3 0 016 0V9" stroke="currentColor" strokeWidth="1.5" fill="none"/>
              </svg>
            </span>
            <span>Your session is protected and expires automatically after inactivity.</span>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Login;
